import { useEffect, useMemo, useState } from "react";
import { calculatePortfolio } from "../lib/analysis/risk.js";
import { formatNumber, formatPercent, formatUsd } from "../lib/utils/formatters.js";
import Panel from "./Panel.jsx";

const STORAGE_KEY = "gold-market-dashboard-portfolio";

export default function PortfolioTracker({ currentPrice = 0 }) {
  const [goldAmountOz, setGoldAmountOz] = useState(1.2);
  const [averageBuyPrice, setAverageBuyPrice] = useState(4200);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (Number.isFinite(Number(saved.goldAmountOz))) setGoldAmountOz(Number(saved.goldAmountOz));
      if (Number.isFinite(Number(saved.averageBuyPrice))) setAverageBuyPrice(Number(saved.averageBuyPrice));
    } catch {
      // localStorage опционален для MVP.
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ goldAmountOz, averageBuyPrice }));
  }, [goldAmountOz, averageBuyPrice]);

  const portfolio = useMemo(
    () => calculatePortfolio({ goldAmountOz, averageBuyPrice, currentPrice }),
    [goldAmountOz, averageBuyPrice, currentPrice],
  );

  return (
    <Panel title="Трекер портфеля" icon="wallet">
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className="text-xs font-medium text-muted">Количество золота</span>
          <div className="mt-1 flex items-center rounded-md border border-white/10 bg-ink/40 px-3">
            <input
              className="min-w-0 flex-1 bg-transparent py-2 text-sm text-white outline-none"
              type="number"
              min="0"
              step="0.01"
              value={goldAmountOz}
              onChange={(event) => setGoldAmountOz(Number(event.target.value))}
            />
            <span className="text-xs text-slate-400">oz</span>
          </div>
        </label>
        <label>
          <span className="text-xs font-medium text-muted">Средняя цена покупки</span>
          <div className="mt-1 flex items-center rounded-md border border-white/10 bg-ink/40 px-3">
            <input
              className="min-w-0 flex-1 bg-transparent py-2 text-sm text-white outline-none"
              type="number"
              min="0"
              step="1"
              value={averageBuyPrice}
              onChange={(event) => setAverageBuyPrice(Number(event.target.value))}
            />
            <span className="text-xs text-slate-400">USD</span>
          </div>
        </label>
      </div>

      {portfolio.valid ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Output label="Текущая стоимость" value={formatUsd(portfolio.currentValue)} />
          <Output
            label="P/L"
            value={formatUsd(portfolio.profitLoss)}
            tone={portfolio.profitLoss >= 0 ? "text-mint" : "text-rose"}
          />
          <Output
            label="P/L %"
            value={formatPercent(portfolio.profitLossPercent)}
            tone={portfolio.profitLossPercent >= 0 ? "text-mint" : "text-rose"}
          />
          <Output label="Безубыток" value={`${formatUsd(portfolio.breakEvenPrice)} · ${formatNumber(goldAmountOz, 3)} oz`} />
        </div>
      ) : (
        <p className="mt-4 rounded-md border border-rose/30 bg-rose/10 p-3 text-sm text-rose">{portfolio.warning}</p>
      )}
    </Panel>
  );
}

function Output({ label, value, tone = "text-white" }) {
  return (
    <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
      <div className="text-xs text-muted">{label}</div>
      <div className={`mt-1 text-sm font-semibold ${tone}`}>{value}</div>
    </div>
  );
}
